import React, { useState, useEffect } from 'react';
import { v1 as uuidv1 } from 'uuid';
import CircularProgress from '@material-ui/core/CircularProgress';
import dotProp from 'dot-prop-immutable';

/* Icons */
import ChevronIcon from '~/../symbols/chevron-down.svg';
import StarIcon from '~/../symbols/star.svg';
import StarBorderedIcon from '~/../symbols/star-bordered.svg';

/* Common */
import { Icon } from '~/components/common';

/* Utilities */
import { trans } from '~/utilities';

export default function MultiSelectList({
    filter,
    filterIndex,
    parentFilters,
    updateFilters,
    className,
    maxItems = 6,
    ...passProps
}) {
    const updateValue = (event, choiceIndex) => {
        const { checked } = event.target;
        const newFilters = dotProp.set(parentFilters, `${filterIndex}.choices.${choiceIndex}.active`, checked);

        updateFilters(newFilters);
    };

    const updateFilter = (choiceIndex, filters) => {
        const newFilters = dotProp.set(parentFilters, `${filterIndex}.choices.${choiceIndex}.filters`, filters);

        updateFilters(newFilters);
    };

    const id = uuidv1();

    return (
        <ul className={className}>
            <CheckBoxList
                choices={filter.choices.slice(0, maxItems)}
                {...{
                    filter,
                    updateValue,
                    updateFilter,
                    startIndex: 0,
                    ...passProps
                }}
            />
            {filter.choices.length > maxItems && (
                <>
                    <div className="collapse" id={`collapse-${id}`}>
                        <CheckBoxList
                            choices={filter.choices.slice(maxItems)}
                            {...{
                                filter,
                                updateValue,
                                updateFilter,
                                startIndex: maxItems,
                                ...passProps
                            }}
                        />
                    </div>
                    <li className="list-group-item">
                        <button
                            className="btn btn-none btn--multi-select-collapse p-0"
                            type="button"
                            data-toggle="collapse"
                            data-target={`#collapse-${id}`}
                        >
                            <Icon
                                size={10}
                                className={`btn__icon icon icon--lg-12`}
                                url={ChevronIcon?.url}
                            />
                            <span className="btn__text text-collapsed">{trans('app.content_block.vehicles.filter.show_more')}</span>
                            <span className="btn__text text-expanded">{trans('app.content_block.vehicles.filter.show_less')}</span>
                        </button>
                    </li>
                </>
            )
            }
        </ul>
    );
}

const CheckBoxList = ({
    choices,
    filter,
    nameMapping,
    suffix,
    updateValue,
    updateFilter,
    startIndex,
    showCount,
    showStars,
    showInactive = true,
}) => {
    return (
        <>
            {choices.map((choice, index) => {
                const id = uuidv1();
                const choiceIndex = index + startIndex;

                if (!showInactive && !choice.active) return null;

                return (
                    <li key={choiceIndex} className="list-group-item">
                        <CustomCheckBox
                            id={id}
                            filter={filter}
                            choice={choice}
                            updateValue={(e) => {
                                updateValue(e, choiceIndex);
                            }}
                            nameMapping={nameMapping}
                            suffix={suffix}
                            showStars={showStars}
                            showCount={showCount}
                        />

                        {/* Level Deeper */}
                        {(choice.filters && choice.filters.length > 0) &&
                            choice.active &&
                            choice.filters.map((filter, filterIndex) => {
                                if (typeof filter.choices.length !== 'undefined' && filter.choices.length > 0) {
                                    return (
                                        <MultiSelectList
                                            className="list-group list-group-flush pl-5"
                                            parentFilters={choice.filters}
                                            filter={filter}
                                            filterIndex={filterIndex}
                                            updateFilters={(filters) => {
                                                updateFilter(choiceIndex, filters);
                                            }}
                                            key={filterIndex}
                                            showStars={showStars}
                                            showCount={showCount}
                                        />
                                    );
                                }
                            })}
                    </li>
                );
            })}
        </>
    )
}

const CustomCheckBox = ({ id, filter, choice, updateValue, nameMapping, suffix, showCount = true , showStars = false}) => {
    const [isUpdating, setIsUpdating] = useState(false);

    useEffect(() => {
        setIsUpdating(false);
    }, [choice.active]);

    const onChange = (event) => {
        setIsUpdating(true);
        updateValue(event);
    };

    const capitalizeFirstLetter = (string) => {
        return string.charAt(0).toUpperCase() + string.slice(1);
    }

    return (
        <div className={`custom-control custom-checkbox position-relative`}>
            {isUpdating && (
                <div className="custom-control__loading">
                    <CircularProgress classes={{ root: 'loading loading--circular-progress' }} />
                </div>
            )}
            <input
                id={id}
                name={`${filter.filterKey}[]`}
                className="custom-control-input"
                type="checkbox"
                value={choice.name}
                checked={choice.active}
                onChange={onChange}
            />

            <label className="custom-control-label d-flex align-items-center" htmlFor={id}>
                {!showStars ? (
                    <span className="mr-1">
                        {(filter.filterKey === 'experience' && choice.name === '0') ? trans('app.content_block.vacancy_overview.filters.experience.none') : (nameMapping ? nameMapping[choice.name] : choice.name ? capitalizeFirstLetter(choice.name) : '')}
                        {suffix && !(filter.filterKey === 'experience' && choice.name === '0') ? suffix : ''}
                    </span>
                ) : (
                    <div className="d-flex align-items-center">
                        <span className="mr-4">{choice.name}</span>
                        {[...Array(choice.name * 1)].map((choice, index) => (
                            <Icon
                                size={24}
                                key={index}
                                className="text-primary"
                                url={StarIcon?.url}
                            />
                        ))}
                        {[...Array(5 - (choice.name * 1))].map((choice, index) => (
                            <Icon
                                size={24}
                                key={index}
                                className="text-primary"
                                url={StarBorderedIcon?.url}
                            />
                        ))}
                    </div>
                )}
                {(showCount && choice.count > 0) && (
                    <span className="text--xs text-muted--60 ml-auto">{choice.count}</span>
                )}
            </label>
        </div>
    );
};
